import axios from "axios";
import { eventBus } from "@/main";
import { validateClinicBranchParams } from "@/utils/routeValidation";

const moduleReceipt = {
  namespaced: true,
  state: {
    receiptList: [],
    receiptTotalNo: null
  },
  mutations: {
    setReceiptList(state, data) {
      state.receiptList = data ? data : [];
    },
    setReceiptScrollList(state, data) {
      state.receiptList = state.receiptList.concat(data);
    },
    setReceiptTotalNo(state, data) {
      state.receiptTotalNo = data;
    },
    cancelReceipt(state, id) {
      var receipt = state.receiptList.find(function(receipt) {
        return receipt.id == id;
      });
      if (receipt) {
        receipt.cancel = true;
      }
    }
  },
  actions: {
    createReceipt({ commit }, payload) {
      // Validate clinic/branch parameters before making API call
      if (!validateClinicBranchParams(payload.clinicUrl, payload.branchUrl, 'createReceipt')) {
        const error = new Error('Invalid clinic/branch parameters for createReceipt');
        error.code = 'INVALID_PARAMS';
        return Promise.reject(error);
      }

      return (
        axios
          .post(
            `/api/receipt/${payload.clinicUrl}/${payload.branchUrl}`,
            payload.data
          )
          .then(res => {
            return res;
          })
          .catch(err => {
            // Automatic error handling via interceptor
            throw err;
          })
      );
    },
    issueReprint({ commit }, payload) {
      // Validate clinic/branch parameters before making API call
      if (!validateClinicBranchParams(payload.clinicUrl, payload.branchUrl, 'issueReprint')) {
        const error = new Error('Invalid clinic/branch parameters for issueReprint');
        error.code = 'INVALID_PARAMS';
        return Promise.reject(error);
      }

      return axios
        .post(
          `/api/receipt/issueReprint/${payload.clinicUrl}/${payload.branchUrl}`,
          payload.data
        )
        .then(res => {
          return res;
        })
        .catch(err => {
          // Automatic error handling via interceptor
          throw err;
        });
    },

    cancelReceipt({ commit }, payload) {
      // Validate clinic/branch parameters before making API call
      if (!validateClinicBranchParams(payload.clinicUrl, payload.branchUrl, 'cancelReceipt')) {
        const error = new Error('Invalid clinic/branch parameters for cancelReceipt');
        error.code = 'INVALID_PARAMS';
        return Promise.reject(error);
      }

      return (
        axios
          .put(
            `/api/receipt/cancel/${payload.clinicUrl}/${payload.branchUrl}`,
            payload.data,
            // The cancel modal handles every error itself (incl. the 409 confirm for
            // receipts already reconciled against an SSO/NHSO statement).
            payload.skipGlobalErrorHandler ? { skipGlobalErrorHandler: true } : undefined
          )
          .then(res => {
            commit("cancelReceipt", payload.data.id);
            return res;
          })
          .catch(err => {
            if (err.response.status === 403) {
              throw err;
            }
            // Automatic error handling via interceptor
            throw err;
          })
      );
    },

    fetchReceiptById({ commit }, payload) {
      // Validate clinic/branch parameters before making API call
      if (!validateClinicBranchParams(payload.clinicUrl, payload.branchUrl, 'fetchReceiptById')) {
        const error = new Error('Invalid clinic/branch parameters for fetchReceiptById');
        error.code = 'INVALID_PARAMS';
        return Promise.reject(error);
      }

      return axios
        .get(
          `/api/reprint/${payload.clinicUrl}/${payload.branchUrl}/${payload.id}`
        )
        .then(res => {
          return res;
        })
        .catch(err => {
          // Automatic error handling via interceptor
          throw err;
        });
    },
    fetchReceiptByHash({ commit }, payload) {
      return axios
        .get(`/api/reprint/${payload.id}`)
        .then(res => {
          return res;
        })
        .catch(err => {
          // Automatic error handling via interceptor
          throw err;
        });
    },

    fetchReceiptList({ commit }, payload) {
      // Validate clinic/branch parameters before making API call
      if (!validateClinicBranchParams(payload.clinicUrl, payload.branchUrl, 'fetchReceiptList')) {
        const error = new Error('Invalid clinic/branch parameters for fetchReceiptList');
        error.code = 'INVALID_PARAMS';
        return Promise.reject(error);
      }

      commit("setReceiptList", []);

      let { offset, limit, search, startDt, endDt, status } = payload;
      return axios
        .get(`/api/receipt/${payload.clinicUrl}/${payload.branchUrl}`, {
          params: {
            offset,
            limit,
            keyword: search,
            startDt,
            endDt,
            status
          }
        })
        .then(res => {
          commit("setReceiptList", res.data.receipts);
          commit("setReceiptTotalNo", res.data.pagination.total);
          return res;
        })
        .catch(err => {
          // Automatic error handling via interceptor
          throw err;
        });
    },

    fetchReceiptScrollList({ commit }, payload) {
      // Validate clinic/branch parameters before making API call
      if (!validateClinicBranchParams(payload.clinicUrl, payload.branchUrl, 'fetchReceiptScrollList')) {
        const error = new Error('Invalid clinic/branch parameters for fetchReceiptScrollList');
        error.code = 'INVALID_PARAMS';
        return Promise.reject(error);
      }

      let { offset, limit, search, startDt, endDt, status } = payload;
      return axios
        .get(`/api/receipt/${payload.clinicUrl}/${payload.branchUrl}`, {
          params: {
            offset,
            limit,
            keyword: search,
            startDt,
            endDt,
            status
          }
        })
        .then(res => {
          commit("setReceiptScrollList", res.data.receipts);
          commit("setReceiptTotalNo", res.data.pagination.total);
          return res;
        })
        .catch(err => {
          // Automatic error handling via interceptor
          throw err;
        });
    },

    //--------- Receipt State ---------//
    cancelStateReceipt({ commit }, id) {
      commit("cancelReceipt", id);
    },

    checkCreateReceipt({ commit }, payload) {
      // Validate clinic/branch parameters before making API call
      if (!validateClinicBranchParams(payload.clinicUrl, payload.branchUrl, 'checkCreateReceipt')) {
        const error = new Error('Invalid clinic/branch parameters for checkCreateReceipt');
        error.code = 'INVALID_PARAMS';
        return Promise.reject(error);
      }

      let searchUrl = `/api/checkCreateReceipt/${payload.clinicUrl}/${payload.branchUrl}/${payload.appointmentId}`;
      return axios
        .get(searchUrl)
        .then(res => {
          return res;
        })
        .catch(err => {
          // Automatic error handling via interceptor
          throw err;
        });
    }
  },
  getters: {
    getReceiptList: state => {
      return state.receiptList;
    },
    getReceiptTotalNo: state => {
      return state.receiptTotalNo;
    }
  }
};
export default moduleReceipt;
